/* eslint-disable consistent-return */
/* eslint-disable no-console */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toasterMsg } from '../Components/Toaster/Toaster';
import { useAblyClient } from '../Pages/Ably/AblyWrapper';
import * as chatActions from '../Pages/Chat/store/Chat.action';
/**
 * Custom hook for handling chat notifications
 * Uses the shared Ably client from AblyWrapper context to avoid creating duplicate connections
 * @param {string} currentUserId - Current user's ID
 * @param {string} activeRoomName - Currently active chat room
 * @param {object} options - { onUnread?: function, showToast?: boolean, isChatOpen?: boolean }
 */
const useChatNotifications = (currentUserId, activeRoomName, selectedSample, options = {}) => {
  const { t } = useTranslation('modules/chat');
  const ablyClient = useAblyClient(); // Use shared client from context instead of creating new connection
  const notificationChannelRef = useRef(null);
  const { onUnread, showToast = false, isChatOpen = false } = options;
  const dispatch = useDispatch();
  useEffect(() => {
    if (!ablyClient || !currentUserId) return;

    // Create notification channel for current user with company URL
    const companyURL = window.location.hostname.split('.')[0] || 'default';
    const clientId = `${currentUserId}_${companyURL}`;
    const notificationChannelName = `notifications:${clientId}`;
    const channel = ablyClient.channels.get(notificationChannelName);
    notificationChannelRef.current = channel;

    // Subscribe to unread message notifications
    const handleUnreadMessage = (message) => {
      console.log('📬 Received unread message notification:', message);

      const { data } = message;
      if (isChatOpen) {
        dispatch(chatActions.markMessagesAsRead(activeRoomName, selectedSample));
      }

      // Suppress only if it's the same room AND chat is open
      const sameRoom = data?.roomName === activeRoomName;
      const shouldSuppress = sameRoom && isChatOpen;
      if (!shouldSuppress) {
        // Show toast notification (optional)
        if (showToast) {
          // Determine module type from room name for better toast messages
          const moduleType = data?.roomName?.startsWith('costing-') ? 'Costing' : 'Sample';
          toasterMsg(
            t('CHAT.NOTIFICATIONS.NEW_MESSAGE_FROM', {
              senderName: data?.senderName || 'Someone',
              roomName: data?.roomName,
              moduleType,
            }),
            'info',
          );
        }
        // Callback for consumers to react (e.g., set unread badge)
        if (typeof onUnread === 'function') {
          try {
            onUnread(data);
          } catch (_e) {
            // ignore callback errors
          }
        }
      }
    };
    // Subscribe to the channel
    channel.subscribe('unread-message', handleUnreadMessage);

    // Cleanup function
    return () => {
      if (notificationChannelRef.current) {
        try {
          notificationChannelRef.current.unsubscribe('unread-message', handleUnreadMessage);
        } catch (_e) {
          // ignore unsubscribe errors
        }
      }
    };
  }, [ablyClient, currentUserId, activeRoomName, t, onUnread, showToast, isChatOpen]);

  return {
    // You can return any state or functions that components might need
    isSubscribed: !!notificationChannelRef.current,
  };
};

export default useChatNotifications;
